<template>
	<!-- .d-flex is display:flex !important and beats v-show's inline display:none; drop flex utilities
	     while the subpage is open so AddressLookup can fully hide the Schedule form (LSP-16820). -->
	<v-card v-show="!isSubpageOpen" :class="[cardClass(), { 'd-flex flex-column fill-height': !isSubpageOpen }]">
		<v-card-text class="flex-grow-1 overflow-y-auto" style="min-height: 0">
			<div v-show="currentState == constants.SCHEDULER_STATE.SCHEDULE">
				<h2 class="mt-3 mb-2"><span class="action-header-schedule">Schedule</span> a Live Video Call</h2>

				<div class="d-flex align-center flex-nowrap ga-2 mb-4 timezone-row" data-test-id="schedule-timezone-row">
					<span class="timezone-label flex-shrink-0">Time Zone</span>
					<v-menu
						v-model="timeZonesOpen"
						location="bottom end"
						:close-on-content-click="true"
						:close-on-click="true"
						:min-width="300"
						:max-height="400"
						tile>
						<template #activator="{ props }">
							<v-btn
								size="small"
								rounded="pill"
								:class="['timezone-btn', 'flex-shrink-1', { open: timeZonesOpen }]"
								:aria-label="`Time Zone: ${timeZoneButtonLabel}`"
								:title="timeZoneButtonLabel"
								:ripple="false"
								v-bind="props">
								{{ timeZoneButtonLabel }}
							</v-btn>
						</template>

						<v-list @click.stop="timeZonesOpen = false">
							<v-list-item
								v-for="zone in schedulingTimezones"
								:key="zone.original"
								link
								@click="selectTimeZone(zone.original)">
								<v-list-item-title v-text="zone.formatted"></v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</div>
				<!-- LSP-17619: the picker's default is now remembered rather than always the device zone,
				     so spell out what the chosen slot means on the viewer's own clock. Sits under the
				     timezone row, which is where the selected zone itself is shown. Hidden whenever both
				     zones read the same time, which is the common case. -->
				<div v-if="crossTimezoneHint" v-test="'schedule-cross-timezone-hint'" class="d-flex timezone-conversion mb-4 text-caption">
					{{ crossTimezoneHint }}
				</div>
				<div class="date-container d-flex justify-space-between ga-2">
					<div class="d-flex flex-grow-1">
						<v-btn color="primary" variant="outlined" rounded="xl" size="large" class="flex-grow-1" @click="selectDate">
							{{ selectedDate }}
						</v-btn>
					</div>
					<div class="d-flex time-pill flex-grow-1">
						<v-menu
							v-model="timeMenuOpen"
							offset-y
							persistent
							bottom
							@click.stop="timeMenuOpen = false"
							max-height="400"
							ref="timeMenuRef"
							:z-index="2010">
							<template #activator="{ props }">
								<v-btn color="primary" variant="outlined" rounded="xl" size="large" class="flex-grow-1" v-bind="props">
									{{ standardTime ? standardTime : "Select a Time" }}
								</v-btn>
							</template>
							<v-list>
								<v-list-item
									v-for="(time, index) in times"
									:id="`time-option-${index}-${autocomplete}`"
									:key="index"
									link
									:class="{ 'v-list-item--active': time.value === scheduledTime }"
									:active="time.value === scheduledTime"
									:input-value="time.value === scheduledTime"
									:disabled="time.disabled"
									@click="setTime(time)">
									<v-list-item-title :class="{ 'selected-time': time.value === scheduledTime }">{{
										time.text
									}}</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>
				</div>
				<v-form ref="form" v-model="form" :autocomplete="autocomplete">
					<!--Contact name-->
					<CustomerAutoComplete
						v-if="!phone || selectedCustomer"
						v-model="selectedCustomer"
						mobile-picker
						label="Contact (Optional)"
						:disabled="isFormDisabled"
						density="compact"
						@update:modelValue="customerSelected" />

					<!--v-if="(!validCustomer && phone && validPhone) || (phone && !validPhone)"-->

					<v-text-field
						v-else
						ref="name"
						v-model="name"
						:autocomplete="autocomplete"
						:disabled="disablePhoneNumberEdit"
						hide-details
						variant="outlined"
						density="compact"
						:max-length="56"
						class="field-border-radius"
						:class="allowReset ? 'reset' : ''">
						<template #label><FieldLabel label="Contact (Optional)" /></template>
					</v-text-field>

					<!--Enter a phone number-->
					<div style="position: relative" @paste.prevent="pasteEvent">
						<v-menu
							v-model="phoneSearchResultsOpen"
							location="bottom"
							:close-on-content-click="false"
							activator="parent"
							:open-on-click="false"
							:min-width="phoneSearchMenuWidth"
							:max-width="phoneSearchMenuWidth"
							max-height="250">
							<v-list v-if="customerItems.length > 0" density="compact">
								<v-list-item
									v-for="item in customerItems"
									:key="item.id"
									@mousedown.prevent
									@click="customerPhoneSelected(item)">
									<v-list-item-title>{{ item.name || item.phone || item.email }}</v-list-item-title>
									<v-list-item-subtitle v-if="item.phone && item.name"
										><span>{{ item.phone }}</span></v-list-item-subtitle
									>
									<v-list-item-subtitle v-if="item.email && (item.name || item.phone)"
										><span>{{ item.email }}</span></v-list-item-subtitle
									>
								</v-list-item>
							</v-list>
							<v-card v-else>
								<v-card-text>No contacts found.</v-card-text>
							</v-card>
						</v-menu>
						<vue-tel-input
							ref="phoneInput"
							:autocomplete="autocomplete"
							tabindex="0"
							:border-radius="0"
							:valid-characters-only="true"
							valid-color="#000000"
							v-bind="vueTelProps"
							v-model="phone"
							:disabled="disablePhoneNumberEdit"
							class="field-border-radius"
							:class="{
								'error--text': showPhoneNumberError
							}"
							:input-options="phoneInputOptions"
							warn-on-typed-country-change
							@validate="validate"
							@on-input="matchPhone"
							@country-changed="matchPhone"
							@blur="phoneBlur"
							@focus="phoneFocus" />

						<!-- LSP-18544: showPhoneFieldErrorState used to colour the field red and say nothing, so a
						     rejected send looked broken rather than fixable. Copy matches the mobile app's
						     ("Please enter a valid phone number.") because the ticket is a web/app parity
						     complaint. vue-tel-input is not a Vuetify field, so the message is rendered directly with
						     Vuetify's own .v-messages typography and text-error colour rather than through v-input. -->
						<div
							v-if="showPhoneNumberError"
							v-test="'schedule-phone-error'"
							:id="phoneErrorMessageId"
							class="v-messages text-error px-3 pt-1"
							role="alert">
							Please enter a valid phone number.
						</div>

						<!--Reset-->
						<span v-if="allowReset" class="reset-span">
							<a href="#" @click="reset()">Reset</a>
						</span>
					</div>

					<ProjectAutoComplete
						ref="projectAutoCompleteRef"
						:model-value="newSelectedProject"
						:disabled="disableProjectField"
						:entity="null"
						density="compact"
						label="Project (Optional)"
						@update:modelValue="updateProject" />
					<AddressLookupLink v-if="!disableProjectField" @openAddressLookup="openAddressLookup" />

					<!-- LSP-20406: tag the session as part of scheduling the call. -->
					<SessionTagPicker v-model="selectedSessionTags" />

					<!--Add a message-->
					<!-- LSP-17482: this field is the ONE place where the counter can't use AppTextarea's app-wide
					     code-point unit, because formatNotificationTemplate (apps/contact-api/src/controllers/conversations.ts)
					     EXPANDS ${organization} / ${user} / ${teamMember} and only then rejects on unicodeLength()
					     — plain UTF-16 length, since it sums `charAt(i).length`. Counting the raw text made both
					     mismatch directions reachable: a short placeholder expanding to a long org name read green
					     and then 400'd, and a raw-over-limit template whose placeholders SHRINK on expansion was
					     blocked even though the server would have taken it. measure-value makes the visible counter
					     AND the over-limit rule measure the exact string the server validates. -->
					<AppTextarea
						ref="message"
						v-model="notificationTemplate"
						:autocomplete="autocomplete"
						rows="3"
						:placeholder="defaultNotificationTemplate"
						:disabled="disablePhoneNumberEdit"
						hide-details="auto"
						variant="outlined"
						:character-limit="serverTemplateCharacterLimit"
						over-limit-message="Please enter a shorter message."
						:measure-value="measureServerTemplateLength"
						class="field-border-radius"
						@change="templateChanged">
						<template #label><FieldLabel label="Message (Required)" /></template>
					</AppTextarea>

					<div v-if="organization.allowEmailInvitations !== false">
						<v-switch
							ref="includeEmailInvitation"
							v-model="includeEmailInvitation"
							inset
							hide-details
							style="margin-top: -12px !important"
							class="mt-0">
							<template #label><FieldLabel label="Include Email Invitation (Optional)" /></template>
						</v-switch>
						<EmailTextField
							v-show="includeEmailInvitation"
							v-if="includeEmailInvitation || email"
							ref="email"
							v-model.trim="email"
							density="compact"
							:disabled="emailEditDisabled"
							class="field-border-radius email-invite-input"
							variant="outlined"
							:required="includeEmailInvitation"
							:max-length="emailMaxLength"
							:rules="emailRules"
							:validate-email="includeEmailInvitation"
							invalid-message="Please enter a valid email or disable the Include Email Invitation option."
							clearable
							hint="Link to join the meeting will be sent to this email."
							persistent-hint>
							<template #label><FieldLabel label="Email (Required)" /></template>
						</EmailTextField>
					</div>

					<SmartPackSelector
						v-model="scribePackSelection"
						ref="smartPackSelector"
						@update:processing="scribePackProcessing = $event"
						@update:user-input-pending="scribeUserInputPending = $event" />
					<v-alert
						v-if="scribeUserInputPending"
						type="info"
						variant="tonal"
						density="compact"
						class="mt-3 cursor-pointer"
						@click="$refs.smartPackSelector?.reopenDialog()">
						<div class="text-body-2">
							This Spark report requires additional input. Click here to provide the required details.
						</div>
					</v-alert>
					<ScheduledSparkNotice v-if="scribePackSelection" :delay-seconds="contentNotificationDelay" />
				</v-form>
			</div>
			<div v-show="currentState == constants.SCHEDULER_STATE.SELECT_DATE" class="mb-4">
				<div class="header">
					<h2>Choose a Date:</h2>
				</div>
				<v-date-picker
					v-model="scheduledDate"
					header-color="var(--schedule-picker)"
					width="100%"
					control-variant="modal"
					:min="minimumDate"
					:max="maximumDate"
					@change="dateSelected"></v-date-picker>
				<v-row justify="center"> </v-row>
			</div>
			<AppointmentConfirmation
				v-if="appointment"
				v-show="currentState == constants.SCHEDULER_STATE.CONFIRMATION"
				:appointment="appointment"
				:time-zone="timeZone"
				:name="name"
				:phone="phone"></AppointmentConfirmation>

			<v-card-actions class="d-flex pa-0 pt-4 ga-2">
				<!--Close-->
				<v-btn
					v-if="currentState == constants.SCHEDULER_STATE.SCHEDULE || currentState == constants.SCHEDULER_STATE.CONFIRMATION"
					style="flex: 1"
					size="x-large"
					rounded="xl"
					variant="outlined"
					elevation="0"
					@click.stop.prevent="close"
					@touchstart.stop.prevent="close()">
					{{ currentState == constants.SCHEDULER_STATE.CONFIRMATION ? "Close" : "Cancel" }}
				</v-btn>

				<!--Start-->
				<v-btn
					v-if="currentState == constants.SCHEDULER_STATE.SCHEDULE && currentState != constants.SCHEDULER_STATE.CONFIRMATION"
					color="primary"
					style="flex: 1"
					size="x-large"
					rounded="xl"
					variant="flat"
					elevation="0"
					:loading="saving"
					:disabled="startButtonDisabled()"
					@click="schedule()">
					Schedule
				</v-btn>

				<!-- Back -->
				<v-btn
					v-if="currentState != constants.SCHEDULER_STATE.SCHEDULE && currentState != constants.SCHEDULER_STATE.CONFIRMATION"
					style="flex: 1"
					size="x-large"
					rounded="xl"
					variant="outlined"
					elevation="0"
					@click="returnToSchedule">
					Back
				</v-btn>

				<!-- Join -->
				<v-btn
					v-if="currentState === constants.SCHEDULER_STATE.CONFIRMATION"
					color="primary"
					style="flex: 1"
					size="x-large"
					rounded="xl"
					variant="flat"
					elevation="0"
					@click="join">
					<v-icon left> icon-video </v-icon>
					Join
				</v-btn>
			</v-card-actions>
		</v-card-text>
	</v-card>
	<AddressLookup v-if="isAddressLookupOpen" @projectSelected="updateProject" @close="isAddressLookupOpen = false" />
</template>

<script>
	import { useId } from "vue";
	import api from "../api";
	import { resolveProjectPrefill } from "@/helpers/projectPrefill";
	// logWarn, not console.warn: Datadog forwards only console.error, so a bare warn is invisible in
	// production — where these diagnostics are the whole point. logWarn still writes to the console.
	import { logInfo, logWarn } from "@/helpers/logError";
	import "../helpers/emoji";
	import tokenHandler from "../helpers/tokenHandler";
	import moment from "moment-timezone";
	import { getDateTokens } from "../helpers/dateFormat";
	import constants from "../api/constants";
	import Dialogs from "../helpers/dialogs";
	import AppointmentConfirmation from "./AppointmentConfirmation.vue";
	import { EventBus } from "../helpers/eventBus";
	import ProjectAutoComplete from "@/components/formfields/ProjectAutoComplete.vue";
	import SessionTagPicker from "@/components/tags/SessionTagPicker.vue";
	import DeviceUtility from "@/helpers/device";
	import { closeActionDialog } from "@/helpers/closeActionDialogs";
	import CustomerAutoComplete from "@/components/formfields/CustomerAutoComplete.vue";
	import { FIELD_LENGTHS, VuetifyValidation } from "../helpers/Validation";
	import {
		calculateTimeout,
		getMessageFromError,
		getTrimmedPhoneFromClipboard,
		serverNotificationTemplateLength
	} from "../helpers/utilities";
	import AddressLookup from "./location/AddressLookup.vue";
	import AddressLookupLink from "./location/AddressLookupLink.vue";
	import { getSearchLabelForCustomers, getCustomerSearchLabel } from "../formatters/customers";
	import { getVueTelProps } from "@/helpers/phone";
	import { SafeVueTelInput as VueTelInput } from "@/components/SafeVueTelInput";
	import "vue-tel-input/vue-tel-input.css";
	import { useToast } from "vue-toastification";
	import { useOrientationChange } from "@/composables/useOrientationChange";
	import SmartPackSelector from "./scribe/SmartPackSelector.vue";
	import ScheduledSparkNotice from "./scribe/ScheduledSparkNotice.vue";
	import { formatTimezoneAbbr } from "../helpers/formatTimezoneAbbr";
	import { getSchedulingTimezones } from "../helpers/ianaTimezoneOptions";
	import {
		resolveSchedulingTimezone,
		formatCrossTimezoneHint,
		resolveBookableInstant,
		isNonexistentWallClock,
		resolveZoneLabelInstant
	} from "../helpers/schedulingTimezone";
	import { renderScheduledCallTokens, applyScheduledCallMessageTokens } from "@/helpers/scheduledCallMessage";

	// LSP-21581: how often the zone chip and cross-timezone hint re-read the clock. Matches
	// RescheduleCallDialog; the only clock-sensitive output here is which fall-back occurrence is
	// still bookable, so nothing finer is needed.
	const CLOCK_TICK_INTERVAL_MS = 15000;
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { AppTextarea } from "@common-ui/components";

	// Smallest gap between "now" and a slot this component will offer as the DEFAULT. Anything less
	// is future on paper and unusable in practice.
	const DEFAULT_SLOT_MINIMUM_LEAD_MS = 60_000;

	export default {
		setup() {
			const { orientationChangeCount } = useOrientationChange();
			return {
				toast: useToast(),
				orientationChangeCount,
				// LSP-18544: per-instance so aria-describedby can never point at a duplicate id. ActionDialog
				// keeps several of these dialogs mounted at once in its v-window, and ActionVideo in
				// particular is mounted twice (sync live-call + async request-content).
				phoneErrorMessageId: `schedule-phone-error-message-${useId()}`
			};
		},
		components: {
			FieldLabel,
			AppointmentConfirmation,
			ProjectAutoComplete,
			SessionTagPicker,
			CustomerAutoComplete,
			AddressLookup,
			AddressLookupLink,
			VueTelInput,
			SmartPackSelector,
			ScheduledSparkNotice,
			AppTextarea
		},
		emits: ["close-menu"],
		mixins: [closeActionDialog],
		props: {
			projectId: String,
			project: {
				type: Object,
				required: false
			},
			conversationType: String, //sync or async
			customerId: String,
			actionText: String,
			actioningText: String,
			allowJoin: Boolean,
			buttonIcon: String,
			showHeader: Boolean
		},
		data() {
			return {
				emailMaxLength: FIELD_LENGTHS.EMAIL,
				isAddressLookupOpen: false,
				showPhoneFieldErrorState: false,
				autocomplete: Math.random().toString(),
				saving: false,
				form: false,
				currentTab: null,
				vueTelProps: getVueTelProps(),
				starting: false,
				validPhone: false,
				autoSetName: false,
				selectedCustomerId: null,
				autoSetNameValue: "",
				emailDisabled: false,
				phone: "",
				email: "",
				emptyEmail: true,
				name: "",
				includeEmailInvitation: false,
				meetingUrl: "",
				actioningTextOverride: "",
				organization: {},
				autoStartRecordings: true,
				notificationTemplate: "",
				// The RENDERED default, shown as the message field's placeholder. Kept separate from
				// notificationTemplateSource below: the placeholder must read like finished copy, while
				// the restore target must still carry its ${...} placeholders so it can be re-rendered.
				defaultNotificationTemplate: "",
				// LSP-20627: the RAW org template. templateChanged() restores this and re-renders it, so
				// a cleared message comes back quoting the currently selected slot rather than the date
				// and time that happened to be selected when the dialog opened.
				notificationTemplateSource: "",
				workingNotificationTemplate: "",
				currentDate: moment(),
				minimumDate: moment().format("YYYY-MM-DD"),
				// LSP-16538: enforce a max year so the picker's next-year arrow can't run past the
				// displayed range. 52 years matches Vuetify's default display window (current year + 52).
				maximumDate: moment().add(52, "years").endOf("year").format("YYYY-MM-DD"),
				scheduledDate: moment().format("YYYY-MM-DD"),
				minimumTime: moment().format("HH:mm"),
				scheduledTime: "",
				currentPeriod: moment().format("a"),
				currentState: constants.SCHEDULER_STATE.SCHEDULE,
				appointment: {},
				conversation: {},
				timeZone: null,
				// LSP-17619: the asynchronous remembered-preference seed in mounted() must never overwrite a
				// choice the user already made. Two flags, because they gate different things: a zone pick
				// suppresses only the zone seed, while a date/time pick suppresses the date/time defaults AND
				// the zone seed (seeding a zone under an already-chosen wall time silently moves it).
				timeZonePicked: false,
				dateTimePicked: false,
				// Set immediately before a PROGRAMMATIC scheduledDate assignment, and consumed by that
				// assignment's watcher run. The watcher calls dateSelected() on every change because
				// v-date-picker's own events proved unreliable — but dateSelected() means "the user committed
				// a date": it closes the calendar and advances currentState. When the remembered-zone seed or
				// the past-day clamp writes the field while the user has the picker OPEN, that fires as a
				// confirm the user never made. Only ever set alongside an assignment that actually changes the
				// value, so it cannot outlive its watcher run and suppress the next genuine pick.
				suppressNextDateConfirm: false,
				constants: constants,
				me: null,
				customers: [],
				selectedCustomer: null,
				validCustomer: true,
				searchCustomers: null,
				phoneSearchResultsOpen: false,
				customerJustSelected: false,
				invalidDate: false,
				timeZonesOpen: false,
				timeMenuOpen: false,
				// LSP-20627: the token values updateTemplate() rendered LAST time, owned by
				// updateTemplate() alone. They used to be five separate fields that the watchers
				// recomputed from their own old values — and `previousScheduledDate` was set to a raw
				// "YYYY-MM-DD" rather than the rendered "M/DD/YYYY h:mm A ZZZ" string, so the
				// ${scheduledDate} run in an already-rendered message was never matched as a whole.
				previousScheduledCallTokens: null,
				times: [],
				newSelectedProject: null,
				// LSP-20406: tags applied to the session at creation.
				selectedSessionTags: [],
				projectSelectItems: [],
				searchTerm: "",
				phoneNumberFieldInFocus: false,
				phoneSearchMenuWidth: null,
				disableProjectField: false,
				scribePackSelection: null,
				scribePackProcessing: false,
				scribeUserInputPending: false,
				// LSP-21581: `moment()` is not reactive, so a computed that resolves the picked slot against the
				// real clock (which occurrence of a fall-back overlap is still bookable) is cached until a
				// REACTIVE dependency changes. A dialog left open across the transition would keep showing the
				// occurrence that had already passed while schedule() submits the other one. The zone chip and
				// the cross-timezone hint read this counter so a tick invalidates them. Same mechanism as
				// RescheduleCallDialog.
				clockTick: 0,
				// Timer handle, not UI state. Cleared in beforeUnmount.
				clockIntervalId: null
			};
		},
		computed: {
			contentNotificationDelay() {
				return this.me?.config?.contentNotificationDelaySeconds;
			},
			timeZoneFormatted() {
				if (!this.timeZone) {
					return "";
				}
				// LSP-21581: the abbreviation and offset belong to the BOOKED date, not to today. Read at
				// the picked slot so a 2 November call booked in October says EST / GMT-05:00, the same
				// answer the invitation message, the session card, the SMS and the email already give.
				void this.clockTick;
				const labelInstant = resolveZoneLabelInstant(this.scheduledDate, this.scheduledTime, this.timeZone);
				const abbr = formatTimezoneAbbr(labelInstant.format("z"));
				return `(GMT${labelInstant.format("Z")}) ${this.timeZone} ${abbr}`.replaceAll("_", " ");
			},
			timeZoneButtonLabel() {
				return this.timeZoneFormatted || "Select time zone";
			},
			// Built ONCE, not per render. getSchedulingTimezones() walks ~590 zone ids and calls moment.tz()
			// for each to format the offset label; in a v-for that ran on every re-render of the dialog.
			// The list cannot change during the dialog's lifetime — it is derived from the tzdb the bundle
			// shipped with.
			schedulingTimezones() {
				return getSchedulingTimezones();
			},
			// LSP-17619: now that the picker's default is remembered rather than always local, show
			// what the chosen slot means on the viewer's own clock. Null (and so hidden) whenever the
			// two zones read the same, which is the common case.
			crossTimezoneHint() {
				void this.clockTick;
				return formatCrossTimezoneHint(this.scheduledDate, this.scheduledTime, this.timeZone);
			},
			isSubpageOpen() {
				return this.isAddressLookupOpen;
			},
			dynamicActioningText() {
				if (this.actioningTextOverride) {
					return this.actioningTextOverride;
				}
				return this.starting ? this.actioningText : this.actionText;
			},
			dynamicName() {
				return this.nameOverride ?? this.name;
			},
			isFormDisabled() {
				return !!this.meetingUrl;
			},
			disablePhoneNumberEdit() {
				return !!this.meetingUrl;
			},
			allowReset() {
				return this.meetingUrl;
			},
			invitationEmail() {
				return this.includeEmailInvitation ? this.email : "";
			},
			emailEditDisabled() {
				return this.emailDisabled || !this.includeEmailInvitation || this.disablePhoneNumberEdit;
			},
			standardTime() {
				if (!this.scheduledTime) {
					return "";
				}

				return this.scheduledTime;
			},
			scheduledName() {
				return this.name ?? this.phone;
			},
			selectedDate() {
				const dateToken = getDateTokens(this.organization?.dateFormat).weekdayMonthDayShortNoYear;
				if (!this.scheduledDate) {
					return this.currentDate.format(dateToken);
				}

				return moment(this.scheduledDate).format(dateToken);
			},
			scheduledDisplayTime() {
				if (!this.appointment) {
					return "";
				}

				const start = moment(this.appointment.startAt);
				const end = moment(this.appointment.endAt);
				const display = `${start.format("h:mma")} - ${end.format("h:mma")}, ${start.format(getDateTokens(this.organization?.dateFormat).weekdayMonthDayOrdinalNoYear)}`;
				return display;
			},
			icsText() {
				if (!this.appointment) {
					return "";
				}

				let icsText = `data:text/calendar;charset=utf-8,${encodeURIComponent(this.appointment.icsText)}`;
				return icsText;
			},
			customerItems() {
				return getSearchLabelForCustomers(this.customers);
			},
			emailRules() {
				if (!this.includeEmailInvitation) {
					return [];
				}
				const errorTpl = "Please enter a valid email or disable the Include Email Invitation option.";
				return [VuetifyValidation.RequiredValidationField(errorTpl).isValid];
			},
			serverTemplateCharacterLimit() {
				// The server's own two limits, keyed off the same conversation type it keys off
				// (formatNotificationTemplate: 1000 for an async request, 500 otherwise). Compared
				// case-insensitively because the server derives isAsyncRequest with constants.areEqual,
				// which upper-cases both sides — a strict === here would cap a "Async" request 500 short of
				// what the server accepts. break-it round 1
				const requestedType = String(this.conversationType ?? "").toUpperCase();
				return requestedType === String(constants.CONVERSATION_TYPE.ASYNC).toUpperCase() ? 1000 : 500;
			},
			phoneInputOptions() {
				// LSP-18544: points the phone input at the error message so a screen reader meets it when
				// tabbing control-to-control, not only when role="alert" fires on insertion. Overriding
				// input-options after v-bind="vueTelProps" is the same pattern WebFormV2.vue uses;
				// aria-describedby is one of vue-tel-input's documented InputOptions keys.
				return {
					...this.vueTelProps.inputOptions,
					"aria-describedby": this.showPhoneNumberError ? this.phoneErrorMessageId : undefined
				};
			},
			showPhoneNumberError() {
				// LSP-18544: true exactly when the user has attempted (or blurred) AND the number would
				// still fail schedule()/start()'s guard. Mirroring the guard's own condition rather than
				// just `!validPhone` matters: vue-tel-input does not always re-emit @validate when the
				// field is cleared, so an emptied field can sit with a stale validPhone === true — which
				// still blocks submit, and would otherwise block it in silence all over again. Drives the
				// red field state as well as the message, so the two can never disagree.
				return this.showPhoneFieldErrorState && !(this.phone && this.validPhone);
			},
			measureServerTemplateLength() {
				// Bound to AppTextarea's measure-value, so the counter and the over-limit rule both report the
				// UTF-16 length of the placeholder-EXPANDED template — the string formatNotificationTemplate
				// actually validates. The placeholder values must be the ones the server picks, which is why
				// getTeamMemberName() mirrors its fallback order exactly (LSP-17482).
				const organizationName = this.organization?.name ?? "";
				const userName = this.getTeamMemberName();
				return (value) => serverNotificationTemplateLength(value, { organizationName, userName });
			}
		},
		watch: {
			searchCustomers(searchTerm) {
				this.searchTerm = searchTerm;
				clearTimeout(this._searchTimerId);
				this._searchTimerId = setTimeout(async () => {
					const customers = await Dialogs.getCustomers(searchTerm);
					this.customers = customers;
				}, 500);
			},
			scheduledDate: {
				handler(newDate) {
					// Consumed unconditionally, before any early exit, so a programmatic write can never
					// leave it set for the user's next pick.
					const wasProgrammatic = this.suppressNextDateConfirm;
					this.suppressNextDateConfirm = false;
					// Manually call dateSelected since v-date-picker events seem unreliable or superseded by v-model updates
					if (!wasProgrammatic && this.currentState === constants.SCHEDULER_STATE.SELECT_DATE) {
						this.dateSelected();
					}

					this.updateMinimumDate(newDate);
					this.updateTemplate();
				}
			},
			scheduledTime: {
				handler() {
					this.updateTemplate();
				}
			},
			notificationTemplate: {
				handler(newText) {
					this.workingNotificationTemplate = newText;
				}
			},
			phone: {
				async handler(newValue) {
					if (!newValue) {
						this.customerJustSelected = false;
						this.deactivateCustomerPhoneSearchMenu();
						const customers = await Dialogs.getCustomers(newValue);
						this.customers = customers;
						return;
					}

					if (this.customerJustSelected) {
						// The user just selected one of search results so we don't want to do a search/open the menu
						return;
					}

					clearTimeout(this._searchTimerId);
					this._searchTimerId = setTimeout(async () => {
						const customers = await Dialogs.getCustomers(newValue);
						this.customers = customers;

						if (customers.length > 0) {
							this.activateCustomerPhoneSearchMenu();
						} else {
							this.deactivateCustomerPhoneSearchMenu();
						}
					}, 500);
				}
			},
			timeMenuOpen(val) {
				if (val && this.scheduledTime) {
					const index = this.times.findIndex((t) => t.value === this.scheduledTime);
					if (index !== -1) {
						setTimeout(() => {
							const el = document.getElementById(`time-option-${index}-${this.autocomplete}`);
							if (el) {
								el.scrollIntoView({ block: "center" });
							}
						}, 100);
					}
				}
			},
			orientationChangeCount() {
				this.$refs.projectAutoCompleteRef?.blur?.();
				this.deactivateCustomerPhoneSearchMenu();
				this.syncPhoneSearchMenuWidth();
				this.$refs.phoneInput?.blur?.();
			}
		},
		async mounted() {
			this.startClock();
			// order matters here
			this.reset();
			this.tryOpenAddressLookup();
			this.me = await api.getMe();

			this.organization = this.me.organization;
			this.autoStartRecordings = this.organization.autoStartRecordings;
			// LSP-17265: AppTextarea's modelValue prop is typed as string (unlike a native v-textarea,
			// which silently tolerates undefined) — guard against an org payload missing this field.
			this.notificationTemplate = this.organization.notificationTemplateScheduled ?? "";
			this.workingNotificationTemplate = this.notificationTemplate;
			this.notificationTemplateSource = this.notificationTemplate;
			// LSP-17619: the dialog is interactive while the api.getMe() above is still in flight, so a
			// user can open the Time Zone menu and choose before this point is reached. Everything
			// below seeds DEFAULTS — never re-apply them over a choice the user has already made, or
			// their pick (and the date/time they set with it) is silently reverted a moment later.
			// Mirrors the guards on mobile's ScheduleCallViewModel. Note the two kinds of "choice" are
			// tracked separately (timeZonePicked vs dateTimePicked) and suppress different defaults —
			// see each branch below. An earlier version used one flag for both, which is what let a date
			// tap silently destroy the stored preference.
			if (!this.timeZonePicked) {
				// Start on the zone this user last scheduled with, falling back to the device zone when
				// they have never picked one. Must stay ahead of populateTimes() and the minimum-date
				// logic below — both derive their floors from this.timeZone.
				//
				// Gated on timeZonePicked ONLY — deliberately no longer on dateTimePicked as well.
				// Suppressing the zone seed because the user touched the date was strictly worse than the
				// slot-movement it was avoiding: the submitted zone then fell back to the device guess, and
				// the API persists whatever is submitted, so a Manila scheduler who tapped a date before
				// /me resolved had her stored Chicago preference REPLACED with Asia/Manila. Losing the
				// preference is silent and permanent; re-reading a picked wall clock in the remembered zone
				// is visible in the same breath, because the zone label and the conversion line below both
				// change with it. updateMinimumDate() re-derives the floors in the new zone further down.
				this.timeZone = resolveSchedulingTimezone(this.me?.user?.schedulingTimezone);
			} else {
				// The web half of the same skip mobile logs in seedRememberedTimeZone(). Correct
				// behaviour — an explicit pick outranks the seed — but it is also the branch a
				// "my remembered zone did not come back" report lands on, and silently it is
				// indistinguishable from getMe() having failed or returned nothing.
				//
				// logInfo, not logWarn: nothing here is wrong. Same leveling as the default-slot
				// branch below — warn is for a condition someone should look at, and a user who
				// opened the picker before a network call finished is not one.
				logInfo("[LSP-17619] Remembered scheduling timezone not applied: the user picked a zone before getMe() resolved", {
					pickedTimeZone: this.timeZone,
					storedSchedulingTimezone: this.me?.user?.schedulingTimezone ?? null
				});
			}
			// No `else if (!this.timeZone)` fallback here any more, deliberately. It existed while the branch
			// above was also gated on dateTimePicked, when a date-only pick skipped the seed and left timeZone
			// null (blank picker, `timeZone: null` submitted). Now that only timeZonePicked suppresses the
			// seed, the branch above runs on every path where timeZone could still be unset, and
			// resolveSchedulingTimezone always returns a zone — so the fallback was unreachable and its
			// comment described a case that can no longer happen. timeZonePicked is set ONLY in
			// selectTimeZone(), which assigns this.timeZone on the very next line.
			this.populateTimes();
			this.updateTemplate();
			// After updateTemplate(), so the field's placeholder reads as finished copy rather than
			// as ${...} token syntax. The restore target is notificationTemplateSource, set above.
			this.defaultNotificationTemplate = this.notificationTemplate;
			// LSP-17619: read "now" in the SELECTED zone, not the browser's. Before the sticky preference
			// existed the picker always sat on the device zone, so browser-local was the same thing. It no
			// longer is: a scheduler in Manila defaulting to Chicago would get Manila's calendar day as the
			// floor and lose Chicago's still-valid today (the LSP-17242 / LSP-14670 class of bug, which
			// updateMinimumDate() already guards on the picker path). Identical output to the old code
			// whenever the two zones agree, i.e. for every user who has never picked a timezone.
			const start = moment().tz(this.timeZone);
			const remainder = 15 - (start.minute() % 15);
			const defaultSlot = this.firstFutureDefaultSlot(moment(start).add(remainder, "minutes"));
			// Assigned FIELD BY FIELD, not gated as one block on !dateTimePicked.
			//
			// reset() clears scheduledTime to "" before the getMe() await, and dateSelected() sets
			// dateTimePicked WITHOUT setting a time — so skipping the whole block left scheduledTime empty,
			// and schedule() then returned early forever with no message and no log: the Schedule button
			// looked dead. updateMinimumDate()'s own touch-up could not rescue it either, being itself
			// gated on `if (this.scheduledTime)`.
			//
			// This is the same "don't overwrite a choice" / "don't skip initializing" conflation that the
			// timeZone fallback below already had to fix once. Seeding each field only when it is unset
			// covers both halves: a user who picked a date keeps it, and the fields they did not pick still
			// get a value.
			//
			// `startTime` used to be seeded here too. It was write-only — nothing but its own guard ever
			// read it once defaultSlot took over — so a reader could not tell it was inert.
			if (!this.dateTimePicked || !this.scheduledDate) {
				const seededDate = defaultSlot.format("YYYY-MM-DD");
				// Flagged only when the value actually changes, because an unchanged assignment fires no
				// watcher and would strand the flag. getMe() above is a real network call after
				// invalidateMe(), and the date field stays tappable across it — so this write can land
				// with the calendar open. It differs from the displayed day exactly when the remembered
				// zone sits on a different calendar day from the device: the Asia-scheduler-books-Chicago
				// case this whole change exists for.
				this.setScheduledDateProgrammatically(seededDate);
			}
			if (!this.dateTimePicked || !this.scheduledTime) {
				// "h:mm A" with no leading zero on the hour, matching the strings populateTimes() puts in
				// the v-select — otherwise the initial value is not highlighted in the dropdown.
				this.scheduledTime = defaultSlot.format("h:mm A");
			}
			// minimumDate is deliberately NOT set here: updateMinimumDate() below runs on every path and
			// re-derives it in the selected zone, so assigning it here would only ever be overwritten.
			// Re-derive the floors in the SELECTED zone, on every path. minimumTime is seeded
			// browser-local in data() and only updateMinimumDate() recomputes it against this.timeZone,
			// while populateTimes() disables every slot below it. The scheduledDate watcher would
			// normally do this, but it fires only when the date actually CHANGES — and whenever the
			// seeded zone shares a calendar day with the device (most of the day, for nearby zones) it
			// does not. That left a browser-local floor applied to a different zone: slots still valid
			// in the selected zone disabled, and already-past ones left clickable.
			this.updateMinimumDate(this.scheduledDate);

			if (this.project) {
				this.newSelectedProject = this.project;
			} else if (this.projectId) {
				const result = await api.getProject(this.projectId);
				this.newSelectedProject = result;
			}

			if (this.projectId || this.project) {
				this.disableProjectField = true;
			}

			this.customers = await Dialogs.getCustomers();

			// LSP-16205: when opened from a project, default the contact to the project's
			// first assigned contact so the user isn't forced to re-search. An explicit
			// customerId prop still takes precedence, and the prefilled value stays editable.
			const { project: resolvedProject, customerId: prefillCustomerId } = await resolveProjectPrefill({
				customerId: this.customerId,
				projectId: this.projectId,
				project: this.newSelectedProject ?? this.project
			});
			if (resolvedProject) {
				this.newSelectedProject = resolvedProject;
			}
			if (prefillCustomerId) {
				try {
					const result = await api.getCustomers({
						where: {
							id: prefillCustomerId
						}
					});

					if (result.count > 0) {
						const customer = getCustomerSearchLabel(result.rows[0]);
						this.setCustomer(customer);
					}
				} catch (error) {
					// A failed prefill is non-critical — degrade to "no contact prefilled" rather than
					// aborting the rest of mounted() (the click-outside listener below runs on this path).
					console.warn(`[LSP-16205] contact prefill lookup failed for customer ${prefillCustomerId}`, error);
				}
			}

			document.addEventListener("click", this.handleClickOutside);
		},
		beforeUnmount() {
			this.stopClock();
			document.removeEventListener("click", this.handleClickOutside);
		},
		methods: {
			startClock() {
				if (this.clockIntervalId !== null) {
					return;
				}
				this.clockIntervalId = setInterval(() => {
					this.clockTick += 1;
				}, CLOCK_TICK_INTERVAL_MS);
			},
			stopClock() {
				if (this.clockIntervalId === null) {
					return;
				}
				clearInterval(this.clockIntervalId);
				this.clockIntervalId = null;
			},
			syncPhoneSearchMenuWidth() {
				this.$nextTick(() => {
					const phoneInputEl = this.$refs.phoneInput?.$el || null;
					const phoneInputRect = phoneInputEl?.getBoundingClientRect?.() || null;
					this.phoneSearchMenuWidth = phoneInputRect?.width ? Math.round(phoneInputRect.width) : null;
				});
			},
			tryOpenAddressLookup() {
				const params = new URLSearchParams(window.location.search);
				const goTo = params.get("goTo");
				if (goTo === "address") {
					this.openAddressLookup();
				}
			},
			openAddressLookup() {
				this.isAddressLookupOpen = true;
			},
			async updateProject(project) {
				this.isAddressLookupOpen = false;
				this.newSelectedProject = project;
			},
			isAdmin() {
				if (this.me?.account.roles[0].name.toLowerCase() === "user") {
					return false;
				}
				return true;
			},
			populateTimes() {
				let hours, minutes, ampm;
				this.times = [];

				for (var i = 0; i < 1440; i += 15) {
					hours = Math.floor(i / 60);
					minutes = i % 60;

					if (minutes < 10) {
						minutes = "0" + minutes; // adding leading zero
					}

					ampm = hours % 24 < 12 ? "AM" : "PM";
					hours = hours % 12;

					if (hours === 0) {
						hours = 12;
					}

					let disabled = false;

					// Minimum time will only be set if the date is set to the current day

					if (this.minimumTime) {
						const minTimeHours = Number.parseInt(this.minimumTime.split(":")[0]);
						const minTimeMinutes = Number.parseInt(this.minimumTime.split(":")[1]);
						const minTime = minTimeHours * 60 + minTimeMinutes;
						disabled = i < minTime;
					}

					const time = `${hours}:${minutes} ${ampm}`;
					// LSP-17619: a spring-forward gap slot used to be offered like any other and booked an
					// hour away from itself, silently -- moment normalizes a nonexistent wall clock forward
					// rather than refusing it. Disabling it here, rather than only rejecting it at submit,
					// means the hour that does not exist is never offered at all, and it re-uses the floor's
					// own machinery: updateMinimumDate() calls this on every date and zone change, and the
					// block right after that call already moves scheduledTime off a slot that came back
					// disabled.
					//
					// Reachable only since this PR. The picker used to sit on the device's own zone, where a
					// user cannot be looking at a gap they are not currently living through. A remembered
					// zone means a Manila scheduler can be looking straight at Chicago's.
					const isNonexistent = isNonexistentWallClock(this.scheduledDate, time, this.timeZone);
					this.times.push({
						text: time,
						value: time,
						disabled: disabled || isNonexistent
					});
				}
			},
			selectTimeZone(zone) {
				// LSP-17619: an explicit pick wins over the remembered seed, even if /me resolves after.
				this.timeZonePicked = true;
				this.timeZone = zone;
				this.updateMinimumDate(this.scheduledDate);
				this.updateTemplate();
			},
			// LSP-17619: return the first candidate slot that is still in the future WHEN READ BACK THE
			// WAY schedule() READS IT. The candidate is an instant, but the picker only keeps wall-clock
			// strings, and schedule() reparses those in the selected zone. During a DST fall-back the
			// same wall time occurs twice and the reparse resolves to the FIRST occurrence, so a
			// default computed from the second one submits an instant up to the overlap earlier —
			// in the past. Comparing the round-tripped value, rather than the instant we started
			// from, is what makes the default agree with its consumer. A no-op outside an overlap.
			firstFutureDefaultSlot(candidate) {
				for (let attempt = 0; attempt < 12; attempt++) {
					const asSubmitted = moment.tz(
						`${candidate.format("YYYY-MM-DD")} ${candidate.format("h:mm A")}`,
						"YYYY-MM-DD h:mm A",
						// Same `?? guess()` default its callers use. Not reachable today — every caller assigns
						// timeZone first — but this helper reads instance state, so it should be total on its
						// own rather than relying on call order. An inline default, deliberately, rather than an
						// unreachable `else` branch: there is nothing here that can go stale unnoticed.
						this.timeZone ?? moment.tz.guess()
					);
					// A MINIMUM LEAD, not merely "after now". `isAfter(moment())` accepts a slot one second
					// away, which is not offerable: the user cannot pick a contact and a phone number before
					// it passes, and schedule()'s past-slot guard then refuses the very default this helper
					// handed them. Seen for real — CI booked a 5:15 PM default and submitted at 5:15:13.
					// A BARE compare, deliberately, and not routed through resolveBookableInstant the way
					// schedule() is. Review suggested that change; it cannot work here, and the round-6
					// adversarial test "never seeds a past slot during Pacific/Chatham's 45-minute-offset
					// fall-back overlap" is what catches it.
					//
					// Inside a fall-back overlap BOTH occurrences share the same wall clock — that is what
					// makes it ambiguous. This function returns a wall clock, so it cannot express "the
					// second occurrence": resolving here only makes the CHECK pass while the value returned
					// still reads as in the past to every naive consumer (populateTimes' floor,
					// updateMinimumDate, and the slot the user sees). Stepping past the overlap is the only
					// answer representable in the type this returns.
					//
					// schedule() is different because it works in instants, not wall clocks: by then the
					// user has picked, and resolving to the later occurrence is both possible and right.
					// The divergence is real and intended, not the drift it looks like.
					if (asSubmitted.valueOf() - moment().valueOf() >= DEFAULT_SLOT_MINIMUM_LEAD_MS) {
						return candidate;
					}
					candidate = moment(candidate).add(15, "minutes");
				}
				// Bounded so a pathological zone cannot spin here; 3 hours clears every real-world
				// fall-back overlap (the longest is an hour; Chatham and Lord Howe are 45 and 30 min).
				//
				// Reaching here means every candidate in that window still read as past once round-tripped
				// through the selected zone, so this returns a slot the caller will show as a default even
				// though it failed the very check this helper exists to apply. Unreachable with today's tzdb
				// — which is exactly why it needs a log rather than silence: if a future tzdb change or an
				// unforeseen zone makes it reachable, "the default slot is in the past" is otherwise
				// untraceable.
				logWarn("[LSP-17619] Exhausted the future-slot search; returning a candidate that failed every check", {
					candidate: candidate.format("YYYY-MM-DD h:mm A"),
					timeZone: this.timeZone ?? moment.tz.guess()
				});
				return candidate;
			},
			updateMinimumDate(newDate) {
				const tz = this.timeZone ?? moment.tz.guess();
				const now = moment().tz(tz);
				const nowBase = now.clone().startOf("day");

				// The earliest selectable calendar day is "today" in the SELECTED timezone — never the
				// browser/device's today. A user scheduling U.S. calls from a device that is hours ahead
				// (e.g. the Philippines, +13 vs Chicago) has already rolled over to "tomorrow" locally while
				// it is still "today" in the U.S. zone. Anchoring the date-picker floor to the device date
				// would then wrongly forbid the valid U.S. "today". Keep the floor in the selected zone so the
				// time-floor logic below (already timezone-aware since LSP-14670 / LSP-15741) is the only gate.
				this.minimumDate = nowBase.format("YYYY-MM-DD");

				// v-date-picker emits a Date at browser-local midnight; moment.tz(date, tz) would reinterpret that absolute instant
				// in tz and can shift the calendar day backward. Normalize to "YYYY-MM-DD" so the parse is tz-naive.
				const dateString =
					newDate instanceof Date
						? `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, "0")}-${String(newDate.getDate()).padStart(2, "0")}`
						: newDate;
				const date = moment.tz(dateString, tz);
				let dateBase = date.startOf("day");

				// LSP-17619: clamp a day that is already PAST in the selected zone up to the floor.
				//
				// Reachable since the remembered-zone seed stopped being suppressed by a date pick: a user
				// on a device BEHIND the remembered zone (LA device, Kiritimati preference — 21h) picks
				// their local today, then the seed re-reads it in a zone that has already crossed midnight,
				// so the chosen day is yesterday there. Setting minimumDate alone does not rescue it — the
				// picker floor only constrains the NEXT pick, while the already-set scheduledDate stands.
				// And the time floor below makes it worse rather than better: for a past day it takes the
				// `else` branch and disables only times before the current clock time, leaving every later
				// slot on that past day enabled. schedule() has no past-instant check of its own, so the
				// booking goes through in the past.
				//
				// Mobile does this with coerceFutureSelection in applySeededTimeZone; this is web's half of
				// the same guard, and both halves have to move together. Placed here rather than at the seed
				// because selectTimeZone() routes through this method too, so an explicit pick of a
				// far-ahead zone is covered by the same line.
				if (dateBase.isBefore(nowBase)) {
					// logInfo, not logWarn — same reasoning as the no-slot-left branch below.
					//
					// This is a designed CORRECTION, not a failure: whenever a caller hands us a day the
					// selected zone has already passed, pulling it to the floor is the intended behaviour.
					// Warn-level proved that empirically — it made four pre-existing specs fail, all of which
					// pin hardcoded dates (2026-03-09) that have since gone stale, so the clamp was doing
					// exactly its job. A diagnostic that fires on ordinary fixtures is a level too high.
					// Still forwarded to Datadog, so "why did my day move?" stays answerable.
					//
					// Genuine anomalies stay at warn: an unrecognised stored zone, a suppressed conversion
					// hint, a device zone the picker refuses, a submit blocked with nothing red on screen.
					logInfo("[LSP-17619] Clamped a scheduled day the selected zone has already passed", {
						from: dateString,
						to: this.minimumDate,
						timeZone: tz
					});
					// Assigning scheduledDate re-enters this method through its watcher, and that watcher
					// calls dateSelected() when currentState === SELECT_DATE. That is NOT harmless just
					// because dateTimePicked is read only inside mounted()'s seeding block — reasoning about
					// the flag alone misses the visible half: dateSelected() also closes the calendar and
					// advances currentState. Suppressed here for the same reason as the seed; a clamp is not
					// a user picking a day.
					this.setScheduledDateProgrammatically(this.minimumDate);
					dateBase = nowBase.clone();
				}

				if (dateBase.isAfter(nowBase)) {
					this.minimumTime = undefined;
				} else {
					this.minimumTime = now.format("HH:mm");
				}

				this.populateTimes();

				if (this.scheduledTime) {
					const index = this.times.findIndex((t) => t.value === this.scheduledTime);
					if (index !== -1 && this.times[index].disabled) {
						// FORWARD from the disabled slot, not the first enabled slot of the day (LSP-17619).
						// Identical while the only reason to disable a slot was the minimum-time floor: that
						// disables a PREFIX, so the first enabled slot and the first enabled slot after the
						// current one are the same entry. The spring-forward gap is the first reason that
						// disables a band in the MIDDLE of the day, and there the two differ badly -- a user
						// who lands on Chicago's 2:30 AM would be thrown back to 12:00 AM instead of nudged
						// to 3:00 AM.
						//
						// No global fallback on purpose. Reaching the end of the list still means the day is
						// exhausted and the branch below rolls to the next one, which is what it did before;
						// the alternative, an enabled slot EARLIER in the day, is only ever a past one.
						const firstAvailable = this.times.slice(index + 1).find((t) => !t.disabled);
						if (firstAvailable) {
							this.scheduledTime = firstAvailable.value;
						} else {
							// LSP-17619: nothing is left on this day in the selected zone, so bumping the TIME
							// cannot help — the day itself has to move.
							//
							// Reachable in the last slot-interval of the zone's day: the clamp above lifts a past
							// scheduledDate to today, minimumTime then becomes the current clock time, and past
							// 11:45 PM no generated slot clears it. Without this branch firstAvailable is
							// undefined, the previous (past) scheduledTime stays exactly where it was, and
							// schedule() submits a past instant — the very thing the clamp exists to prevent.
							//
							// firstFutureDefaultSlot walks forward in 15-minute steps on a moment, so it crosses
							// midnight on its own and lands on tomorrow's first slot. Reusing it also keeps the
							// DST-overlap handling in one place, and mirrors mobile's nextFutureHalfHour, which
							// rolls into the next day for the same reason. Assigning scheduledDate re-enters this
							// method once via its watcher; that pass sees a future day, clears minimumTime, and
							// settles.
							// logInfo, not logWarn. This branch is the feature working: the selected day is
							// exhausted and the picker rolls forward, which is visible in the date field and
							// happens routinely late in the day. It surfaced two pre-existing specs the moment it
							// was a warning — a good sign it is ordinary rather than anomalous. The past-day
							// clamp above stays a warning, because that one corrects state that should not have
							// existed.
							logInfo("[LSP-17619] No slot left today in the selected zone; moving to the next day", {
								scheduledDate: this.scheduledDate,
								scheduledTime: this.scheduledTime,
								timeZone: tz
							});
							const dayStart = moment().tz(tz);
							const toNextQuarter = 15 - (dayStart.minute() % 15);
							const nextSlot = this.firstFutureDefaultSlot(moment(dayStart).add(toNextQuarter, "minutes"));
							this.setScheduledDateProgrammatically(nextSlot.format("YYYY-MM-DD"));
							this.scheduledTime = nextSlot.format("h:mm A");
						}
					}
				}
			},
			selectDate() {
				this.currentState = constants.SCHEDULER_STATE.SELECT_DATE;
				this.timeMenuOpen = false;
			},
			dateSelected() {
				// LSP-17619: committing a date counts as engaging with the schedule, so the
				// remembered-preference seed in mounted() must not re-apply its defaults over it.
				// Same rule as onDateSelected on mobile's ScheduleCallViewModel.
				this.dateTimePicked = true;
				this.currentState = constants.SCHEDULER_STATE.SCHEDULE;
				this.updateTemplate();
			},
			timeSelected() {
				this.updateTemplate();
			},
			getTeamMemberName() {
				// Mirrors formatNotificationTemplate's placeholder-user resolution exactly (LSP-17482): the
				// counter can only agree with the server if it substitutes the same string the server will.
				// The old `${name} ${lastname}` unconditionally stringified a null firstname, so a
				// lastname-only user got a literal "null Smith" both in the counter AND in the sent message.
				//
				// break-it round 1: `me.user` is optional-chained too (this now runs inside a render-time
				// computed, so a /me payload without a user object would blank the dialog rather than just
				// mis-name a message), and the old `?? "-"` fourth tier is gone — the server's last tier is
				// `email || ""`, so inventing a dash made every ${user} count one unit more than the server
				// measures, in exactly the window before getMe() resolves.
				let name = "";

				if (this.me?.user?.firstname) {
					name = this.me.user.firstname;
				}

				if (this.me?.user?.lastname) {
					name = name ? `${name} ${this.me.user.lastname}` : this.me.user.lastname;
				}

				if (!name) {
					name = this.me?.user?.email;
				}

				return name ?? "";
			},
			updateTemplate() {
				if (!this.workingNotificationTemplate) {
					return;
				}

				// LSP-20627: the substitution itself lives in the shared helper so the booking flow here
				// and the reschedule dialog render the same copy the same way. The helper's invariant is
				// that no substitution ever reads text a substitution has already written — see
				// helpers/scheduledCallMessage.ts. The previously rendered values it needs are the ones
				// IT produced last time, tracked here and nowhere else; the watchers deliberately no
				// longer recompute their own.
				const tokens = renderScheduledCallTokens({
					selectedDate: this.scheduledDate ?? this.currentDate,
					selectedTime: this.scheduledTime,
					timeZone: this.timeZone,
					dateFormat: this.organization?.dateFormat
				});

				const result = applyScheduledCallMessageTokens(
					this.workingNotificationTemplate,
					tokens,
					{ teamMemberName: this.getTeamMemberName(), organizationName: this.organization?.name ?? "" },
					this.previousScheduledCallTokens ?? {}
				);

				this.previousScheduledCallTokens = result.tokens;
				this.notificationTemplate = result.message;
				this.workingNotificationTemplate = result.message;
			},
			confirmation() {
				this.currentState = constants.SCHEDULER_STATE.CONFIRMATION;
			},
			cardClass() {
				return `pa-1 conversation-card ${DeviceUtility.isMobile ? "conversation-bottom" : ""} ${this.conversationType}`;
			},
			startButtonDisabled() {
				return this.saving || !!this.actioningTextOverride || this.scribePackProcessing;
			},
			// The ONLY sanctioned way for CODE to write scheduledDate; the user's own picks come through
			// the v-date-picker's v-model. Exists because the scheduledDate watcher calls dateSelected()
			// on every change — v-date-picker's own events were unreliable — and dateSelected() means "the
			// user committed a day": it closes the calendar and advances currentState. A programmatic write
			// that skips this helper therefore fires as a confirm the user never made.
			//
			// Centralised after this was fixed twice at two sites and a THIRD unguarded site (the
			// no-slot-left rollover) was still found afterwards. Three misses of the same rule means the
			// rule needed one home, not another copy. Any new programmatic write goes through here.
			setScheduledDateProgrammatically(nextDate) {
				// An unchanged assignment emits no change event, so the watcher never runs and never
				// consumes the flag — setting it here would strand it and swallow the user's next real pick.
				if (this.scheduledDate === nextDate) {
					return;
				}
				this.suppressNextDateConfirm = true;
				this.scheduledDate = nextDate;
			},
			reset() {
				this.$refs.smartPackSelector?.reset?.();
				this.customerJustSelected = false;
				this.isAddressLookupOpen = false;
				this.currentState = constants.SCHEDULER_STATE.SCHEDULE;
				this.meetingUrl = "";
				this.phone = "";
				// LSP-18544 break-it round 1: reset() cleared the phone but not the error state, so a
				// blocked submit followed by Reset (or a dialog close, which calls reset() through the
				// closeActionDialog mixin) left "Please enter a valid phone number." accusing an empty
				// field. validPhone goes with it — vue-tel-input does not re-emit @validate on a
				// programmatic clear, so leaving it true would make an empty field look valid.
				this.showPhoneFieldErrorState = false;
				this.validPhone = false;
				this.email = "";
				this.emptyEmail = true;
				this.name = "";
				this.includeEmailInvitation = false;
				// In the SELECTED zone, not the browser's — the same fix mounted() got, on the path left behind.
				// reset() is reachable from the in-session "Reset" link on an already-mounted component and
				// does NOT clear this.timeZone, so re-seeding browser-local pairs a device-zone day with a
				// remembered zone. The scheduledDate watcher's past-day clamp only rescues the case where the
				// device runs BEHIND the selected zone; it cannot pull a day backwards when the device is
				// ahead, and Vue watchers do not fire at all when the reassigned value is unchanged.
				const resetNow = moment().tz(this.timeZone ?? moment.tz.guess());
				// Already safe by ordering — currentState is set to SCHEDULE above, so the watcher's
				// SELECT_DATE branch cannot fire — but routed through the helper anyway so that safety
				// stops depending on the order of two statements twenty lines apart.
				this.setScheduledDateProgrammatically(resetNow.format("YYYY-MM-DD"));
				this.currentPeriod = resetNow.format("a");
				this.scheduledTime = "";
				this.autoSetName = "";
				this.autoSetNameValue = "";
				// Unconditional, for the watcher's change-detection blind spot above: when reset() lands on
				// the same calendar day the field already held, the watcher never runs and minimumTime/times
				// keep floors derived from a different zone.
				this.updateMinimumDate(this.scheduledDate);
				this.selectedCustomer = null;
				this.selectedSessionTags = [];
				// LSP-17265: guard against undefined — this runs before mounted()'s org fetch resolves
				// (this.organization is still {} on first mount), and AppTextarea's modelValue prop is
				// typed as string, unlike a native v-textarea which tolerated undefined silently.
				this.notificationTemplate = this.organization.notificationTemplateScheduled ?? "";
				this.notificationTemplateSource = this.notificationTemplate;
				// The notificationTemplate watcher mirrors this, but not until the next flush, and
				// updateTemplate() reads it on the line below — mirror it here as mounted() does.
				this.workingNotificationTemplate = this.notificationTemplate;
				// LSP-20627: the field is back to the raw template, so there is no previous render left
				// to re-apply values into — drop the stale tokens rather than let them hunt for values
				// that belong to a message this dialog no longer shows.
				this.previousScheduledCallTokens = null;
				this.updateTemplate();
				this.scribePackSelection = null;
				this.scribePackProcessing = false;
				this.scribeUserInputPending = false;
				setTimeout(() => {
					this.$refs?.phoneInput?.focus?.();
				}, 500);
			},
			pasteEvent(event) {
				this.phone = getTrimmedPhoneFromClipboard(event);
				// If a user pastes into the vue-tel-input, force it to reformat itself.
				this.$nextTick(function () {
					try {
						this.$refs.phoneInput.onInput();
					} catch {
						// Ignore formatting refresh failures when the input is not mounted yet.
					}
				});
			},
			// Fires when the correctness of the vue-tel-input's phone number changes (from true to false or vice-versa) and when the component is mounted.
			validate(args) {
				this.validPhone = args.valid;
			},
			// Fires when the vue-tel-input's input changes.
			async matchPhone(number, phoneObject) {
				if (!this.validPhone || !phoneObject || !phoneObject.valid) {
					// bail on invalid numbers
					if (phoneObject && !phoneObject.formatted && this.selectedCustomer?.id) {
						this.clearCustomer();
						return;
					} else if (phoneObject && phoneObject.formatted?.length > 0 && !phoneObject.valid) {
						this.setCustomer(null, true);
						return;
					} else {
						return;
					}
				}

				// Make sure that even if the user changes the formatting of their phone number, we always internally
				// update it to formatted version. This way, when the phone number gets saved to the database, it is
				// always properly formatted.
				this.$nextTick(() => {
					this.phone = phoneObject.formatted;
				});
				this.deactivateCustomerPhoneSearchMenu();

				if (this.phone == "") {
					this.clearCustomer();
					return;
				}

				// Use the formatted phone number
				number = phoneObject?.formatted || number;

				if (!number && this.autoSetName) {
					this.name = "";
				}

				if (this.customerJustSelected) {
					this.customerJustSelected = false;
					return;
				}

				const result = await api.getCustomers({
					where: {
						"$PhoneNumbers.phoneNumber$": {
							$like: `%${number}%`
						}
					}
				});

				if (result.count === 1) {
					const customer = getCustomerSearchLabel(result.rows[0]);
					if (customer) {
						this.customerPhoneSelected(customer);
						clearTimeout(this._searchTimerId);
						return;
					}
				}

				if (result.count > 0) {
					this.customers = result.rows;
					this.activateCustomerPhoneSearchMenu();
					clearTimeout(this._searchTimerId);
				} else {
					this.setCustomer(null, true, this.searchTerm);
				}
			},
			customerSelected(customer) {
				if (customer) {
					this.customerJustSelected = true;
				}

				this.setCustomer(customer);
			},
			customerPhoneSelected(customer) {
				this.setCustomer(customer);
				this.phone = customer.phone || "";
				this.deactivateCustomerPhoneSearchMenu();
				this.customerJustSelected = true;
			},
			phoneBlur() {
				this.phoneNumberFieldInFocus = false;
				this.deactivateCustomerPhoneSearchMenu();
				// LSP-18544 break-it round 2: this LATCHES the flag on, it never clears it. Clearing it here
				// erased the explanation while the submit guard was still blocking — ActionVideo's
				// matchPhone runs on @country-changed with the field blurred, so picking a different country
				// after a blocked send wiped both the red border and the message while start() still bailed
				// on `!this.phone`. Hiding is showPhoneNumberError's job: it goes false the moment the number
				// is actually submittable, so nothing needs to clear the flag except reset().
				if (Boolean(this.phone) && !this.validPhone) {
					this.showPhoneFieldErrorState = true;
				}
			},
			phoneFocus() {
				this.phoneNumberFieldInFocus = true;
				// LSP-18544 break-it round 1: this used to clear the error state on focus. When the error
				// state was colour-only that was harmless; now it would erase the only statement of WHAT
				// to fix at the exact moment the user clicks in to fix it. showPhoneNumberError already
				// hides itself the instant the number becomes valid, so nothing needs clearing here.
				this.syncPhoneSearchMenuWidth();
			},
			activateCustomerPhoneSearchMenu() {
				if (this.phone && this.phone.length > 0 && this.phoneNumberFieldInFocus) {
					this.phoneSearchResultsOpen = true;
					this.syncPhoneSearchMenuWidth();
				}
			},
			deactivateCustomerPhoneSearchMenu() {
				this.phoneSearchResultsOpen = false;
			},
			setTime(time) {
				// LSP-17619: as with dateSelected — a chosen time must survive a late /me seed.
				this.dateTimePicked = true;
				this.scheduledTime = time.value;
			},
			async clearCustomer() {
				this.customerJustSelected = false;
				this.setCustomer(null);
				this.phone = "";
				this.customers = await Dialogs.getCustomers();
			},
			setCustomer(customer, filtering = false, searchTerm = "") {
				this.selectedCustomer = customer;
				if (customer) {
					this.validCustomer = true;
					this.name =
						customer.firstname && customer.lastname
							? `${customer.firstname} ${customer.lastname}`
							: customer.firstname || customer.lastname;
					this.autoSetName = true;
					this.autoSetNameValue = this.name;
					const emailInvitationsAllowed = this.organization.allowEmailInvitations !== false;

					if (customer.EmailAddresses?.length > 0 && emailInvitationsAllowed) {
						this.includeEmailInvitation = true;
						this.email = customer.EmailAddresses[0].emailAddress;
						this.emailDisabled = true;
					} else {
						this.email = "";
						this.emailDisabled = false;
						this.includeEmailInvitation = false;
					}

					if (customer.PhoneNumbers?.length > 0) {
						if (!this.phone || this.customerJustSelected) {
							this.phone = customer.PhoneNumbers[0].phoneNumber;
						}
					} else {
						this.phone = "";
					}
				} else {
					this.validCustomer = false;
					this.name = searchTerm;
					if (this.emailDisabled) {
						this.email = "";
						this.emailDisabled = false;
						this.includeEmailInvitation = false;
					}
					if (!filtering) {
						this.phone = "";
					}
				}
			},
			templateChanged(template) {
				// Reset notification template
				// Fix: Check this.notificationTemplate or event value as template is an Event object in Vue 3
				const value = template?.target?.value ?? template;
				if (!String(value ?? "").trim() || !String(this.notificationTemplate ?? "").trim()) {
					// LSP-20627: restore the RAW template and let updateTemplate() render it for the
					// slot selected right now — restoring the mount-time render re-inserted whatever
					// date and time happened to be selected when the dialog opened. Restoring the raw
					// template also discards the previous render, so the tokens that described it go too.
					this.notificationTemplate = this.notificationTemplateSource;
					this.workingNotificationTemplate = this.notificationTemplate;
					this.previousScheduledCallTokens = null;
					this.updateTemplate();
				}
			},
			async schedule() {
				if (this.startButtonDisabled()) {
					return;
				}
				if (!this.phone || !this.validPhone) {
					// LSP-18544: this gate used to exit silently, so "the Schedule button does nothing" was
					// indistinguishable from "the phone number is malformed". Log which condition fired.
					console.warn("[LSP-18544] ActionSchedule.schedule blocked: phone number not valid", {
						hasPhone: Boolean(this.phone),
						validPhone: this.validPhone,
						conversationType: this.conversationType,
						projectId: this.projectId ?? null,
						customerId: this.selectedCustomerId ?? null
					});
					this.showPhoneFieldErrorState = true;
					return;
				}
				if (!this.form || !this.scheduledDate || !this.scheduledTime) {
					// LSP-17619: logged for the same reason as its LSP-18544 siblings below. validate() marks
					// FIELDS red, but an unset scheduledDate/scheduledTime is not a field the user left
					// blank — it is one the mount path failed to seed, so nothing turns red and the button
					// just does nothing. That is exactly how the dateTimePicked whole-block skip stayed
					// invisible. Scoped to the date/time half on purpose: `!this.form` is an ordinary state
					// (the form ref simply has not validated yet) and logging it is pure noise.
					if (!this.scheduledDate || !this.scheduledTime) {
						logWarn("[LSP-17619] ActionSchedule.schedule blocked: date or time missing", {
							scheduledDate: this.scheduledDate,
							scheduledTime: this.scheduledTime,
							timeZone: this.timeZone,
							dateTimePicked: this.dateTimePicked,
							timeZonePicked: this.timeZonePicked
						});
					}
					await this.$refs.form.validate();
					return;
				}
				if (this.projectId && !this.newSelectedProject) {
					// LSP-18544 break-it round 2: the sibling silent return one line below the phone guard, with
					// the same "the button does nothing" signature. getProject() swallows its fetch error, so a
					// 403/500 on GET /projects/:id leaves this permanently unresolved with no UI feedback at all.
					// CLAUDE.md requires logging at EVERY fail-condition in a gate a silent-failure fix touches.
					console.warn("[LSP-18544] ActionSchedule.schedule blocked: project not resolved", {
						projectId: this.projectId,
						hasSelectedProject: Boolean(this.newSelectedProject)
					});
					return;
				}
				if (this.scribeUserInputPending) {
					return;
				}
				const actionScribePackSelection = this.scribePackSelection;

				this.saving = true;

				try {
					let baseUrl = window.env.VITE_LIVE_DOMAIN;
					if (this.conversationType === "async") {
						baseUrl = window.env.VITE_ASYNC_DOMAIN;
					}

					// Captured BEFORE the await, because this await can rewrite the very fields it is about
					// to submit (Bugbot, medium). api.getMe() is memoized on a single in-flight promise, and
					// mounted() awaits it too — mounted registers its continuation at mount, long before this
					// click, so when /me lands mounted's whole seeding block runs FIRST: it re-anchors
					// this.timeZone to the remembered zone, may call setScheduledDateProgrammatically(), and
					// then updateMinimumDate() re-derives the floors. Only then does this function resume and
					// read the fields. A user who accepted the pre-filled default and clicked Schedule while
					// /me was still in flight would therefore book an instant they never saw — a full network
					// RTT of exposure, not a microtask.
					//
					// Same rule as mobile's isScheduling guard in applySeededTimeZone, and the same trade-off
					// recorded there: once the user has committed, what was on screen is what gets booked,
					// even though submitting a device-zone default then stores it as their preference.
					// Reversing that would change the booking under someone already watching a spinner.
					//
					// All FOUR consumers below use these, deliberately: startAt is derived from
					// submittedDate/submittedTime/submittedTimeZone and the payload's timeZone is the same
					// snapshot, so the booked instant and the zone recorded against it cannot disagree.
					// `timeZone` is null in data() until the seed lands or the user picks, and the picker reads
					// "Select time zone" the whole time (timeZoneButtonLabel). So null does NOT mean "the
					// device zone was on screen" — it means nothing was confirmed, and there is nothing for
					// the snapshot to protect. Pinning it would submit timeZone: null and undo the seed this
					// ticket added. Resolved below, after the await, so that case takes the seeded value —
					// which is exactly what happened before the snapshot existed.
					const confirmedTimeZone = this.timeZone;
					const confirmedDate = this.scheduledDate;
					const confirmedTime = this.scheduledTime;

					const me = await api.getMe();

					// ALL of the snapshot, or none of it (ls-heroku-ci medium). The paragraph above already
					// says null zone means "nothing was confirmed, and there is nothing for the snapshot to
					// protect" -- but the date and time were pinned unconditionally anyway, which made that
					// case book the PRE-seed wall clock under the POST-seed zone. Those two never described
					// the same instant.
					//
					// The reported shape: New York device, Asia/Manila remembered, /me still in flight. The
					// user takes the device-zone default 9:00 AM without touching the zone picker (it still
					// reads "Select time zone"). The seed lands, updateMinimumDate() re-reads 9:00 AM in
					// Manila -- already evening there, past -- and rolls the form to the next day. The
					// restore then dragged it back to 9:00 AM, the past-slot guard refused it, and the form
					// was left on the slot it had just refused. Every later click failed the same way.
					// Worse in the other direction: a seeded zone behind the device books an instant most of
					// a day from what the screen meant, and only the conversion line hints at it.
					//
					// So the snapshot is now conditional on there being something to protect. Nothing
					// confirmed means the seed wins outright -- zone, day and time together -- which is what
					// happened before the snapshot existed and is coherent in a way the mix never was.
					const nothingWasConfirmed = confirmedTimeZone === null;
					const submittedTimeZone = confirmedTimeZone ?? this.timeZone;
					const submittedDate = nothingWasConfirmed ? this.scheduledDate : confirmedDate;
					const submittedTime = nothingWasConfirmed ? this.scheduledTime : confirmedTime;

					// Logged, not silent: this is the condition that used to change the booking invisibly, and
					// without a line here a "it booked a different time" report has nothing to reconstruct
					// from. Expected and harmless in the common case where /me had already resolved.
					if (
						(confirmedTimeZone !== null && this.timeZone !== confirmedTimeZone) ||
						this.scheduledDate !== confirmedDate ||
						this.scheduledTime !== confirmedTime
					) {
						logWarn("[LSP-17619] Slot changed while the submit was in flight; booking the values the user confirmed", {
							// `nothingWasConfirmed` is the field to read first in a "it booked the wrong time"
							// report: it says whether the values below were defended or deliberately handed
							// over to the seed.
							nothingWasConfirmed: nothingWasConfirmed,
							submittedTimeZone: submittedTimeZone,
							currentTimeZone: this.timeZone,
							submittedDate: submittedDate,
							confirmedDate: confirmedDate,
							currentScheduledDate: this.scheduledDate,
							submittedTime: submittedTime,
							confirmedTime: confirmedTime,
							currentScheduledTime: this.scheduledTime
						});
					}

					// Re-render the invitation from the SUBMITTED slot before it is sent (Bugbot, high).
					// The snapshot fixed startAt and the appointment's timeZone, but the copy was still read
					// live off this.notificationTemplate — and mounted()'s seed runs updateTemplate() through
					// the scheduledDate watcher during the same await. The customer was then told one time
					// while startAt recorded another, which is the exact divergence this ticket exists to
					// close. Assigning this.timeZone first keeps the picker, this re-render and the
					// confirmation screen's :time-zone binding all on the zone that was booked.
					//
					// Hoisted ABOVE the past-slot rejection and the updateProject() await, not left just before
					// the payload where it started. Those are the two ways out of this function that are not the
					// booking: a rejected slot returns, and updateProject() can throw into the catch. Restoring
					// afterwards meant neither ran it, so a failed attempt handed the user back a form and an
					// invitation still showing whatever the seed had moved them to — and the retry they make from
					// it sends an instant they never confirmed. Nothing between here and the payload reads these
					// fields (startAt and the tokens both come from the submitted consts), so running it earlier
					// costs nothing on the success path and covers both failures.
					//
					// Safe for a user who typed their own message: applyScheduledCallMessageTokens rewrites
					// only the values IT previously rendered (LSP-20627), which is the same path every slot
					// change already takes.
					this.timeZone = submittedTimeZone;
					// The DATE and TIME too, not just the zone (Bugbot, medium). Restoring only the zone left
					// the other half of the snapshot unapplied: mounted()'s seed can have moved
					// scheduledDate/scheduledTime during the await, and if the booking then FAILS the user is
					// returned to a form showing the seeded slot rather than the one they confirmed — so a
					// retry silently sends a different instant than the attempt they just watched fail.
					//
					// Through setScheduledDateProgrammatically, the sanctioned writer — assigning the field
					// directly here would trip this component's own collectRawScheduledDateWrites guard in
					// ActionSchedule.spec.ts, which scans the source for exactly that. (Spelling the
					// forbidden assignment out in this comment tripped it too, which is a fair reminder that
					// the guard reads text, not syntax.) It no-ops when the value is unchanged, which is the
					// common case where the seed never fired.
					this.setScheduledDateProgrammatically(submittedDate);
					this.scheduledTime = submittedTime;
					const submittedTokens = renderScheduledCallTokens({
						selectedDate: submittedDate ?? this.currentDate,
						selectedTime: submittedTime,
						timeZone: submittedTimeZone,
						dateFormat: this.organization?.dateFormat
					});
					const submittedMessage = applyScheduledCallMessageTokens(
						this.workingNotificationTemplate,
						submittedTokens,
						{ teamMemberName: this.getTeamMemberName(), organizationName: this.organization?.name ?? "" },
						this.previousScheduledCallTokens ?? {}
					);
					this.previousScheduledCallTokens = submittedMessage.tokens;
					this.notificationTemplate = submittedMessage.message;
					this.workingNotificationTemplate = submittedMessage.message;

					this.templateChanged(this.notificationTemplate);

					const selectedUserId = me.user.id;
					const nameParts = (this.name || "").trim().split(" ");
					const first = nameParts.length > 0 ? nameParts[0] : "";
					const last = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
					const dateBase = moment(submittedDate).format("YYYY-MM-DD");
					let userDate = moment.tz(`${dateBase} ${submittedTime}`, "YYYY-MM-DD h:mm A", submittedTimeZone);
					// LSP-17619: the web half of mobile's isSelectedScheduleInPast() re-check. Mobile grew one
					// because its picker stays interactive right up to submit; web's does too, and had none.
					//
					// Placed HERE on purpose — after userDate is built from exactly the values about to be
					// submitted, so it validates the same instant rather than an earlier snapshot, and BEFORE
					// the updateProject() call below, so a rejected booking cannot leave a contact attached to
					// a project as a side effect.
					//
					// Reachable, not theoretical: the zone can still change under the user while this dialog is
					// open (the getMe() seed resolves asynchronously and re-derives every floor), and
					// firstFutureDefaultSlot's own documented fallthrough RETURNS its last candidate rather
					// than blocking when its bounded search is exhausted — it only logs. Nothing downstream
					// catches it either: the Schedule button is deliberately never disabled on invalid state
					// (CLAUDE.md "Form action buttons — always enabled, no-op when invalid"), and the API
					// stores whatever startAt it is handed.
					// moment(), not Date.now(): every other clock read in this component goes through moment, and
					// the default-slot logic this guard backstops is built on it — two time sources could
					// disagree about which side of "now" a slot sits on.
					// The gap's safety net, below the picker's own screening in populateTimes() (LSP-17619).
					// Belt and braces on purpose: populateTimes() stops the user SELECTING a nonexistent wall
					// clock, but scheduledTime is also written by the mounted() seed and by updateMinimumDate()'s
					// clamp, neither of which goes through the picker -- and moment will normalize the gap forward
					// without complaint, so nothing further down can notice. Checked BEFORE resolveBookableInstant
					// because the two DST cases are opposites: an ambiguous clock is real and gets rescued to its
					// later occurrence, a nonexistent one is not real and must be refused.
					if (isNonexistentWallClock(submittedDate, submittedTime, submittedTimeZone)) {
						logWarn("[LSP-17619] ActionSchedule.schedule blocked: the selected wall clock does not exist in this timezone", {
							scheduledDate: submittedDate,
							scheduledTime: submittedTime,
							timeZone: submittedTimeZone,
							// What moment would have silently booked instead, which is the number a
							// "it booked an hour late" report will be holding.
							normalizedTo: userDate.isValid() ? userDate.format("YYYY-MM-DD h:mm A z") : null
						});
						const gapMessage = "That time does not exist on this date because the clocks move forward. Choose another time.";
						this.toast.error(gapMessage, { timeout: calculateTimeout(gapMessage) });
						this.saving = false;
						return;
					}

					const nowMs = moment().valueOf();
					// Same shared resolver the invitation copy and the conversion line use — see
					// helpers/schedulingTimezone.ts. Resolving the fall-back overlap here independently is exactly
					// how the submitted instant drifts from the one the customer is told about.
					userDate = resolveBookableInstant(userDate, nowMs);
					if (!userDate.isValid() || userDate.valueOf() <= nowMs) {
						logWarn("[LSP-17619] ActionSchedule.schedule blocked: the selected slot is not in the future", {
							// The SUBMITTED values, matching the instant actually evaluated above — logging
							// this.* here would describe post-seed state the rejection was not based on.
							scheduledDate: submittedDate,
							scheduledTime: submittedTime,
							timeZone: submittedTimeZone,
							startAt: userDate.isValid() ? userDate.valueOf() : null,
							now: nowMs
						});
						const pastSlotMessage = "Choose a future date and time.";
						this.toast.error(pastSlotMessage, { timeout: calculateTimeout(pastSlotMessage) });
						this.saving = false;
						return;
					}
					if (this.newSelectedProject?.id && this.selectedCustomer?.id) {
						const selectedContact = this.selectedCustomer;
						let contactExistance = false;
						if (!this.newSelectedProject.Customers) {
							this.newSelectedProject.Customers = [];
						}
						for (const contact of this.newSelectedProject.Customers) {
							if (contact.id === selectedContact.id) {
								contactExistance = true;
								break;
							}
						}
						if (!contactExistance) {
							await api.updateProject(this.newSelectedProject.id, {
								Customers: [...this.newSelectedProject.Customers, selectedContact]
							});
						}
					}
					let startAt = userDate.unix() * 1000;

					const includeEmailInvitation = this.organization.allowEmailInvitations !== false && this.includeEmailInvitation;

					// LSP-20406: captured with the request so a selection edit during the awaited create
					// cannot change which tags the appointment's conversation carries.
					const actionSessionTags = [...this.selectedSessionTags];
					const data = {
						appointment: {
							customerId: this.selectedCustomerId ?? undefined,
							userId: selectedUserId,
							startAt: startAt,
							// The snapshot, not this.timeZone: startAt was derived from it, and a booking whose
							// recorded zone disagrees with its own instant is the drift this ticket exists to stop.
							timeZone: submittedTimeZone
						},
						conversation: {
							referrer: baseUrl,
							autoStartRecordings: this.organization.autoStartRecordings,
							forcedUserId: selectedUserId,
							customerId: this.selectedCustomerId ?? undefined,
							notificationSMS: false,
							// submittedMessage, not this.notificationTemplate (Bugbot, medium). Bugbot's scenario --
							// the restore's scheduledDate/scheduledTime watchers flushing during the awaited
							// updateProject() and re-rendering the copy without resolveBookableInstant -- does not
							// reproduce; the adversarial test below drives exactly it (fall-back overlap, awaited
							// project update, a seed that really does move the slot) and the invitation still names
							// the booked occurrence. But that held because of Vue's flush timing, which is not a
							// property this file should depend on to keep the customer's copy honest. Reading the
							// local makes it structural: the body sent is the one rendered from the submitted slot,
							// whatever any watcher does to this.notificationTemplate in between.
							notificationTemplate: submittedMessage.message?.trim(),
							screenShareSupported: this.conversationType != constants.CONVERSATION_TYPE.ASYNC,
							visitors: [
								{
									phone: this.phone?.trim(),
									email: this.email?.trim(),
									firstname: first,
									lastname: last,
									notificationSMS: true,
									notificationEmail: includeEmailInvitation
								}
							],
							type: this.conversationType,
							initiationType: "admin",
							projectId: this.newSelectedProject?.id ?? undefined,
							browserId: tokenHandler.getBrowserId(),
							browserToken: await tokenHandler.getOrRefreshToken(),
							scribePackId: actionScribePackSelection?.pack?.id ?? undefined,
							scribePackName: actionScribePackSelection?.pack?.name ?? undefined,
							scribePackTaskIds: actionScribePackSelection?.taskIds ?? undefined,
							scribePackUserInputData: actionScribePackSelection?.userInputData ?? undefined,
							// LSP-20406: attach the picked tags atomically at creation (server upsertRelatedLabels).
							Labels: actionSessionTags.length ? actionSessionTags : undefined
						}
					};

					// save data
					const result = await api.createAppointment(data);

					if (result.appointment?.id) {
						this.appointment = result.appointment;
						this.conversation = result.conversation;
						this.appointment.icsText = result.icsText;
						this.appointment.googleCalendarUrl = result.googleCalendarUrl;
						this.confirmation();
						EventBus.$emit("UpdateDashboard");
						// LSP-17619: creating the appointment also asked the server to save this timezone as the
						// user's new scheduling default. getMe() is memoized, so drop the cache or the next
						// Schedule dialog in this session would certainly seed from a stale value.
						//
						// Dropping the cache does NOT guarantee the next dialog sees the new preference. The
						// server-side write is deliberately fire-and-forget (awaiting it would put a preference
						// write on the booking response's critical path), so evicting the cache and letting the
						// next getMe() re-fetch RACES that UPDATE — and getMe() short-circuits on a non-null
						// `_me`, so a fetch that won the race cached the OLD zone for the rest of the session
						// rather than self-correcting.
						//
						// So patch rather than evict. This client knows exactly which zone it just submitted
						// and the server persists that same canonical value, which makes the cached copy
						// correct by construction instead of correct-if-the-race-goes-our-way.
						//
						// Runs LAST, after the confirmation is on screen: this is cache bookkeeping for a
						// booking that already succeeded. Its own try/catch because the enclosing catch shows a
						// user-facing error toast, and nothing should put "error" on top of a confirmation the
						// user already has.
						try {
							api.setCachedSchedulingTimezone(data.appointment.timeZone);
						} catch (cacheError) {
							logWarn("[LSP-17619] Could not update the cached /me scheduling timezone after scheduling", {
								error: cacheError instanceof Error ? cacheError.message : String(cacheError)
							});
						}
					}
				} catch (e) {
					const message = getMessageFromError(e);
					this.toast.error(message, { timeout: calculateTimeout(message) });
					console.error(e, {
						tags: {
							method: "schedule",
							file: "ActionSchedule"
						},
						extra: {
							type: this.conversationType,
							phone: this.phone
						}
					});
				}

				this.saving = false;
			},
			returnToSchedule() {
				if (this.invalidDate) {
					return;
				}
				this.currentState = constants.SCHEDULER_STATE.SCHEDULE;
			},
			join() {
				if (this.invalidDate) {
					return;
				}
				const win = window.open(this.conversation.creatorUrl, "_blank");
				if (!win) {
					const message =
						"Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
				}
			},
			handleClickOutside(event) {
				const menuRef = this.$refs.timeMenuRef;
				const menu = menuRef?.$el;

				const targetElement = event.target;
				const containsResult = menu?.contains(targetElement);

				if (menuRef && !containsResult) {
					this.timeMenuOpen = false;
				}
			}
		}
	};
</script>

<style lang="scss" scoped>
	.start-conversation-button {
		font-family: "Inter Tight", sans-serif;
		font-size: 20px;
		color: white;
		margin-left: 10px;
	}
	.reset-span {
		position: relative;
		left: calc(100% - 50px);
		display: inline-block;
		top: -30px;
		height: 0;
	}

	.conversation-card {
		border-radius: 12px;
		display: flex;
		flex-direction: column;
		justify-content: start;
	}

	.conversation-card .header {
		display: flex;
		margin-bottom: 28px;
		margin-top: 12px;
		color: #000;
	}

	.conversation-card .timezone-row {
		color: #000;
	}

	.conversation-card .timezone-label {
		font-weight: 600;
		color: #03011d;
	}

	.conversation-card .timezone-btn {
		background-color: #fff;
		border: 1px solid #03011d;
		color: #03011d;
		font-weight: 600;
		padding: 0 10px;
		min-width: 0;
		max-width: 100%;
	}

	.conversation-card .timezone-btn :deep(.v-btn__content) {
		display: block;
		min-width: 0;
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.conversation-card .timezone-btn.open {
		background-color: #dff0ff;
	}

	.conversation-card textarea {
		color: #212121;
		font-size: 16px;
	}

	.conversation-card h2,
	.conversation-card i {
		font-size: 24px;
		margin-right: 5px;
	}

	.conversation-card .v-card__actions {
		padding: 16px;
		display: flex;
		justify-content: space-evenly;
	}

	.conversation-card .v-card__actions > button.back {
		width: 100%;
	}

	.conversation-card .auto-message {
		color: #b3b3b3;
		font-size: 16px;
	}

	.conversation-card .record-switch {
		margin-left: 5px;
		margin-top: 0px;
	}

	.conversation-card .contact-name.reset {
		padding-top: 0px;
	}

	.v-slide-group__next,
	.v-slide-group__prev {
		display: none;
	}

	/* Email Invite */

	.date-container {
		display: flex;
		margin-bottom: 16px;
	}

	.date-container .time-pill {
		position: relative;
		z-index: 10;
	}

	.date-container .btn-pill {
		width: 100%;
	}

	.date-container .btn-pill:first-child {
		margin-right: 12px;
	}

	.date-container::v-deep .v-menu__content,
	.v-menu__content {
		max-height: 300px !important;
	}

	.team-member-share {
		position: absolute;
		padding-top: 8px;
		margin-left: 2px;
		color: #0070ff;
	}

	.confirmation .schedule-header {
		text-align: center;
	}

	.confirmation hr {
		margin: 16px 0;
		color: #dfe0e2;
	}

	.confirmation .schedule-title {
		margin: 12px 0;
		background: linear-gradient(93deg, #019c9c 0%, #01bcbc 27.62%);
		background-clip: text;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
	}

	.confirmation {
		color: #03011d;
		font-size: 1em;
	}

	.confirmation .time {
		margin-bottom: 8px;
	}

	.confirmation .time,
	.confirmation .timezone {
		line-height: 1.5;
	}

	/* LSP-17619: secondary line under the timezone, so it reads as supporting detail rather than
	   competing with the zone itself. */
	.timezone-conversion {
		line-height: 1.5;
		color: #9b9ea5;
	}

	.confirmation .schedule {
		padding: 0 24px;
		color: #9b9ea5;
	}

	.confirmation .schedule .icon {
		color: #0070ff;
	}

	.confirmation .summary {
		font-size: 0.875em;
		padding: 0 24px;
		margin-bottom: 16px;
		text-align: center;
	}

	.confirmation .calendar-links {
		font-size: 0.875em;
		font-weight: 600;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}

	.confirmation .calendar-links .link {
		display: flex;
		margin-bottom: 16px;
		align-items: center;
		align-self: stretch;
		color: #03011d77;
	}

	.confirmation .calendar-links .link img {
		margin-right: 20px;
	}

	.confirmation .calendar-links .link:nth-child(3) {
		margin-bottom: 0px;
	}

	.invalid-date {
		color: #cf2546;
		font-size: 0.75rem;
	}

	.selected-time {
		color: #0070ff !important;
		font-weight: 600;
	}

	.v-list-item--active .v-list-item__title {
		color: #0070ff !important;
	}
</style>
<style>
	.time-picker-menu {
		max-height: 400px !important;
	}

	.v-time-picker-clock__ampm .v-picker__title__btn:hover {
		cursor: pointer;
	}

	.v-time-picker-clock.theme--light {
		background: #dff0ff;
	}

	.v-time-picker-clock__item {
		color: #5e5c6e;
	}

	.v-time-picker-clock__item.v-time-picker-clock__item--active {
		color: white;
	}

	.vue-tel-input .vti__input {
		border-radius: 0 11px 11px 0;
	}
</style>
